import { IPatientDetails } from "../types/PrescriptionIngestionTypes";
import { MEDICATIONS } from "../config/PrescriptionDefaults";
import {
  getUniqueName,
  getDateOfBirth,
} from "../../../utils/RandomDemographicInfoGen";

const DEFAULT_VALUES = {
  PATIENT_GENDER: "M",
} as const;

/**
 * Creates a patient details object with default values and randomly generated names
 */
export const createEmptyPatientDetails = (): IPatientDetails => ({
  lastName: getUniqueName(),
  firstName: getUniqueName(),
  gender: DEFAULT_VALUES.PATIENT_GENDER,
  dateOfBirth: getDateOfBirth(Math.random(), -1000),
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zipCode: "",
  phoneNumber: "",
});

/**
 * Gets the first available medication from the MEDICATIONS config
 */
export const getInitialMedication = () => {
  const firstMedicationKey = Object.keys(MEDICATIONS)[0];
  if (!firstMedicationKey) {
    throw new Error("No medications available in MEDICATIONS config");
  }
  return {
    key: firstMedicationKey,
    details: MEDICATIONS[firstMedicationKey],
  };
};

/**
 * Generates a random message ID for prescriptions
 */
export const generateMessageId = (): string => {
  const RANDOM_ID_MAX = 1000000;
  return Math.floor(Math.random() * RANDOM_ID_MAX).toString();
};

/**
 * Helper to get PST date/time parts from a Date object
 */
const getPSTParts = (date: Date) => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || "";

  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
    second: get("second"),
    milliseconds: date.getMilliseconds().toString().padStart(3, "0"),
  };
};

/**
 * Gets current timestamp in PST timezone as YYYYMMDDHHMMSSMMM format
 */
export const getPSTTimestamp = (): string => {
  const p = getPSTParts(new Date());
  return (
    p.year + p.month + p.day + p.hour + p.minute + p.second + p.milliseconds
  );
};

/**
 * Gets current date in YYYY-MM-DD format in Pacific timezone (PST/PDT).
 * Re-exported from the leaf dateUtils module so there is a single implementation (also imported by
 * PrescriptionDefaults, which can't import from here due to the MEDICATIONS import cycle).
 */
export { getCurrentDateString } from "./dateUtils";

/**
 * Converts a date to Pacific timezone and returns it as an ISO string.
 * The returned string represents the Pacific time with 'Z' suffix for compatibility.
 * Note: The 'Z' indicates the format is ISO-like, but the time values are Pacific, not UTC.
 */
export const convertToPST = (date: Date): string => {
  const p = getPSTParts(date);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}.${p.milliseconds}Z`;
};

/**
 * Generates S3 key in format: messageID_PSTtimestamp_UUID.xml
 */
export const generateS3Key = (
  messageId: string,
  pstTimestamp: string
): string => {
  const { v4: uuidv4 } = require("uuid");
  const S3_PATH_PREFIX = "inbound/qa_apaq_portal";
  const uuid = uuidv4();
  return `${S3_PATH_PREFIX}/${messageId}_${pstTimestamp}_${uuid}.xml`;
};
